/**
 * /collection — public information page for device collection after repair.
 * Referenced in transactional SMS sent via Twilio.
 */
import React from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, Clock, MapPin, ShieldCheck, ArrowRight, Truck, Home } from "lucide-react";

const STEPS = [
  { icon: Package, title: "Repair completed", body: "Once the repair centre confirms your device is fixed and quality-checked, you receive an SMS and email confirmation with your claim reference." },
  { icon: Clock, title: "Choose your slot", body: "Sign in to your Treasurescare account and open the claim to pick a collection method and time that suits you." },
  { icon: Truck, title: "Delivered back", body: "Devices are returned by insured, tracked courier or ready-for-pickup at your nearest partner location — usually within 2 working days." },
];

const OPTIONS = [
  { icon: Home, title: "Courier delivery", body: "Free next-day tracked courier back to your registered address. Signature required on receipt for your protection." },
  { icon: MapPin, title: "Partner pickup", body: "Collect from one of our authorised partner stores across the UK. Bring photo ID matching the account holder." },
];

export default function Collection() {
  return (
    <PublicShell>
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-16" data-testid="collection-page">
        <div className="max-w-2xl">
          <div className="overline text-secondary flex items-center gap-2">
            <Package className="w-3 h-3" /> After the repair
          </div>
          <h1 className="mt-3 font-display text-4xl lg:text-5xl font-light tracking-tighter">
            Getting your device <span className="text-primary italic">back</span>
          </h1>
          <p className="mt-4 text-muted-foreground">
            When your repair is complete we notify you by SMS and email. From your Treasurescare
            account you can choose how you'd like to receive your device — nothing more to install,
            no unfamiliar links to click.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/app/claims"><Button size="lg" data-testid="collection-cta-claims">Open my claims <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
            <Link to="/login"><Button size="lg" variant="outline">Sign in</Button></Link>
          </div>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-4">
          {STEPS.map((s, i) => (
            <Card key={s.title}>
              <CardContent className="pt-6">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <s.icon className="w-5 h-5" />
                </div>
                <div className="mt-4 text-xs text-muted-foreground uppercase tracking-widest">Step {i + 1}</div>
                <div className="mt-1 font-display text-xl">{s.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12">
          <h2 className="font-display text-2xl">Collection options</h2>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            {OPTIONS.map((o) => (
              <Card key={o.title}>
                <CardHeader className="flex flex-row items-center gap-3 space-y-0">
                  <div className="w-9 h-9 rounded-lg bg-secondary/15 text-secondary flex items-center justify-center">
                    <o.icon className="w-4 h-4" />
                  </div>
                  <CardTitle className="text-base">{o.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">{o.body}</CardContent>
              </Card>
            ))}
          </div>
        </div>

        <Card className="mt-12 bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg">A note on SMS updates</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Treasurescare only sends transactional SMS — repair updates, verification and password
                  reset codes, appointment reminders. We never send marketing SMS. Every message includes
                  a "Reply STOP to opt out" footer, and you can manage all communication preferences from
                  your account settings at any time.
                </p>
                <div className="mt-3">
                  <Link to="/privacy" className="text-sm text-primary underline">Read the privacy policy</Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-12 text-sm text-muted-foreground">
          Trouble accessing your account or need help with collection? Contact us at
          {" "}<a href="mailto:support@treasurescare.com" className="text-primary underline">support@treasurescare.com</a>{" "}
          or visit our <Link to="/contact" className="text-primary underline">contact page</Link>.
        </div>
      </section>
    </PublicShell>
  );
}
